import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MessageCircleQuestion } from 'lucide-react'
import { MY_ASKS_KEY, raiseAsk } from '../api/asks'
import type { ConflictRow } from './ConflictsView'

/**
 * Asking a dancer's family whether a clash is on purpose (D-570).
 *
 * <b>The one answer nobody in the building has.</b> The detector cannot tell a
 * dancer leaving technique early for her solo from one double-booked by
 * mistake, and neither can the studio — which is why the conflicts screen asks
 * for a verdict rather than guessing (D-150). Her mother knows.
 *
 * <b>One press, no dialog.</b> Everything the question needs is on the row:
 * the dancer, the two things, the times. A dialog here would be a form asking
 * her to retype what the screen is already showing, which is the tax that
 * stops a control like this being used.
 *
 * <b>The refusal is the server's own words.</b> The envelope refuses a second
 * open question about the same clash — that is what stops a family being asked
 * the same thing three times on three days — and saying so plainly beats a
 * button that silently does nothing the second time.
 */
/**
 * @param dancer Whose family is being asked, and it is never implied (D-609).
 *
 * One row can name two children now, so a button reading only *Ask the family*
 * on a row showing two dancer badges would leave Tonya believing she had asked
 * about the clash when she had asked one of the two households. The name on the
 * button is what makes the control agree with the row above it.
 */
export default function AskTheFamily({
  row,
  dancer,
}: {
  row: ConflictRow
  dancer: { subjectId: string; subject: string }
}) {
  const queryClient = useQueryClient()

  const ask = useMutation({
    mutationFn: () =>
      raiseAsk({
        kind: 'double-booked',
        subject: { type: 'Dancer', id: dancer.subjectId },
        // The clash in words, which is all a task-shaped ask can carry and is
        // also what the family reads. The server keys the question on it, so
        // two unrelated clashes about one dancer stay two questions.
        content: clashInWords(row),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...MY_ASKS_KEY] })
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  if (ask.isSuccess) {
    return (
      <p role="status" className="text-[12.5px] font-semibold text-good">
        Asked {dancer.subject}&rsquo;s family. Their answer will come back to you.
      </p>
    )
  }

  return (
    <>
      <button
        type="button"
        disabled={ask.isPending}
        onClick={() => ask.mutate()}
        className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-accent px-3.5 text-[13px] font-semibold text-on-accent disabled:opacity-50"
      >
        <MessageCircleQuestion size={14} strokeWidth={2.5} className="shrink-0" />
        Ask {dancer.subject}&rsquo;s family
      </button>

      {ask.isError && (
        <p role="alert" className="w-full text-[12.5px] text-crit">
          {(ask.error as Error).message}
        </p>
      )}
    </>
  )
}

/**
 * The clash as a sentence, which is what the family reads and what the
 * question is keyed on.
 *
 * <b>How often it happens is a property of the pair, not of the dates on the
 * row</b> (D-150, D-88). The conflicts list fetches one week and a class meets
 * on one weekday at one time, so a dancer clash carries exactly one date
 * however many weeks it recurs — a first version read <code>dates.length</code>
 * and told a family a weekly clash happened <i>on one night</i>, which is a
 * different question from the one they would have been answering. The same
 * mistake D-569 had just recorded for the room panel.
 *
 * Two classes both meet every week, so their clash does. A booking is a single
 * reservation, so a class against one private is a one-night problem.
 */
export function clashInWords(row: ConflictRow): string {
  const weekly = row.firstSide.kind === 'Class' && row.secondSide.kind === 'Class'
  const when = weekly ? 'every week they both run' : `on ${row.dates[0]}`

  return `${row.firstSide.label} and ${row.secondSide.label} overlap ${row.overlap}, ${when}.`
}
